import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { salesCycleListResponseSchema, type SalesCycle } from '@complan/contracts';
import { apiFetch } from '@/shared/api/client';
import { queryKeys } from '@/shared/query/query-keys';
import { REFERENCE_STALE_TIME_MS } from '@/shared/api/selectable-master-data';

/**
 * The active Sales_Cycle definitions the intake form's Timing step offers as a
 * date-filling picker (R6.1, R6.6).
 *
 * ## Why unscoped by planning year
 *
 * The calendar reads one year's cycles because it renders one year's grid. The
 * intake form has no year to scope by: an Initiative's planning year is *derived*
 * from its start date (R4.8), so at the moment the user is choosing cycles there
 * is no year yet - choosing is what establishes it. Reading every year's active
 * cycles is therefore the only correct population here, and it is small (the
 * retained window is 5 planning years at roughly six cycles each).
 *
 * ## Active only
 *
 * `includeInactive` is deliberately not set. R6.6 keeps a deactivated cycle out
 * of new assignment proposals, and this picker exists only to propose - an
 * Initiative that already references a since-deactivated cycle keeps it through
 * the stored assignment and the Detail_Panel's display, neither of which reads
 * this hook.
 */
export function useSelectableSalesCycles(): UseQueryResult<readonly SalesCycle[], unknown> {
  return useQuery({
    queryKey: queryKeys.selectableSalesCycles(),
    queryFn: async (): Promise<readonly SalesCycle[]> => {
      const body = await apiFetch<unknown>('/api/sales-cycles');
      return salesCycleListResponseSchema.parse(body).items;
    },
    staleTime: REFERENCE_STALE_TIME_MS,
  });
}

/**
 * The date range a set of selected cycles implies: the earliest selected start
 * and the latest selected end.
 *
 * Pure, and exported for its own test: this is the rule the user asked for
 * ("start date from the earliest sales cycle, end date with the latest"), and it
 * is the one place that rule is expressed. Returns `null` for an empty selection
 * so the caller can leave the entered dates untouched rather than clearing them.
 *
 * Dates are compared as strings, which is exact for the `YYYY-MM-DD` wire form -
 * fixed width and zero padded, so lexicographic order *is* chronological order
 * (the same property `@complan/domain`'s `comparePlainDates` relies on).
 */
export function dateRangeOfCycles(cycles: readonly SalesCycle[]): {
  readonly startDate: SalesCycle['startDate'];
  readonly endDate: SalesCycle['endDate'];
} | null {
  const first = cycles[0];
  if (first === undefined) return null;
  let startDate = first.startDate;
  let endDate = first.endDate;
  for (const cycle of cycles) {
    if (cycle.startDate < startDate) startDate = cycle.startDate;
    if (cycle.endDate > endDate) endDate = cycle.endDate;
  }
  return { startDate, endDate };
}
